// QA test runs: one document per run of the external black-box suite (kind 'automated') or per manual
// test session (kind 'manual'), posted by the QA engineers' machines to the /auto-tests-results and
// /manual-tests-results endpoints (server/rest/testResults.ts) and read on the super-user /test-results page.
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, FileMeta, GetMethodResponse, ListMethodArg, ListMethodResponse } from '/imports/types';
import { escapeRegExp } from '/imports/utils';
import { MILLIS_24_HOURS } from '/imports/constants';

export type TestRunKind = 'automated' | 'manual';
export const testRunKinds: TestRunKind[] = ['automated', 'manual'];
/** Where each kind is posted. Declared here so the route that serves it and the panel that tells QA about
 *  it cannot drift apart. */
export const testRunEndpoints: { [K in TestRunKind]: string } = {
  'automated': '/auto-tests-results',
  'manual': '/manual-tests-results',
};

export type TestOutcome = 'passed' | 'failed' | 'skipped';

export interface TestRunResult {
  name: string;
  status: TestOutcome;
  suite?: string;
  durationMs?: number;
  message?: string;
  tags?: string[];
}

export interface TestRunTotals {
  passed: number;
  failed: number;
  skipped: number;
  total: number;
}

export interface TestRun extends Doc {
  kind: TestRunKind;
  receivedAt: Date;
  startedAt: Date;
  finishedAt?: Date;
  durationMs?: number;
  label?: string;
  targetUrl?: string;
  gitSha?: string;
  host?: string;
  platform?: string;
  totals: TestRunTotals;
  /** Percentage of executed (passed + failed) tests that passed, 0 when nothing was executed. */
  passRate: number;
  results: TestRunResult[];
  pdf?: FileMeta;
}

export const TestRuns = new Mongo.Collection<TestRun>('testRuns');

Meteor.isServer && Meteor.startup(async () => {
  await TestRuns.createIndexAsync({ kind: 1, startedAt: -1 });
});

/** List runs (without their per-test results) for the /test-results page. Super users only. */
export const listTestRuns = DeclareMethod<ListMethodArg<TestRun>, ListMethodResponse<TestRun>>('listTestRuns');
/** One run with its per-test results. Super users only. */
export const getTestRun = DeclareMethod<string, GetMethodResponse<TestRun>>('getTestRun');
/** Remove a run and its stored report. Super users only. */
export const deleteTestRun = DeclareMethod<string>('deleteTestRun');
/** The headline metrics for each kind of run. Super users only. */
export const getTestRunsSummary = DeclareMethod<undefined, TestRunsSummary>('getTestRunsSummary');

/** The key each endpoint expects, for the Api Keys panel. Super users only, and fetched when somebody
 *  asks rather than published, so a key is only ever in a page that was opened on purpose. */
export type TestResultsKeys = { [K in TestRunKind]?: string };
export const getTestResultsKeys = DeclareMethod<undefined, TestResultsKeys>('getTestResultsKeys');

const OUTCOME_ALIASES: { [alias: string]: TestOutcome } = {
  'passed': 'passed', 'pass': 'passed', 'ok': 'passed', 'success': 'passed', 'succeeded': 'passed',
  'failed': 'failed', 'fail': 'failed', 'failure': 'failed', 'error': 'failed', 'errored': 'failed', 'broken': 'failed',
  'skipped': 'skipped', 'skip': 'skipped', 'pending': 'skipped', 'todo': 'skipped', 'disabled': 'skipped', 'ignored': 'skipped',
};

/**
 * Folds the status vocabularies of the common runners (jest, mocha, Playwright, pytest) onto the three
 * outcomes stored here.
 * @param status - the status as the poster wrote it
 * @returns the outcome, or undefined when the word is not one of the known ones
 */
export const normalizeTestOutcome = (status: unknown): TestOutcome | undefined => {
  if (typeof status !== 'string') return undefined;
  return OUTCOME_ALIASES[status.trim().toLowerCase()];
};

/**
 * Counts a run's results by outcome.
 * @param results - the per-test results
 * @returns the totals
 */
export const testRunTotals = (results: Pick<TestRunResult, 'status'>[]): TestRunTotals => {
  const totals: TestRunTotals = { 'passed': 0, 'failed': 0, 'skipped': 0, 'total': results.length };
  for (const result of results) totals[result.status] += 1;
  return totals;
};

/**
 * The share of executed tests that passed, to one decimal. Skipped tests are left out of both sides: a
 * run that skipped half its suite should not read as half failing.
 * @param totals - the run's totals
 * @returns a percentage 0..100, or 0 when nothing was executed
 */
export const testRunPassRate = (totals: Pick<TestRunTotals, 'passed' | 'failed'>): number => {
  const executed = totals.passed + totals.failed;
  if (!executed) return 0;
  return Math.round((totals.passed / executed) * 1000) / 10;
};

/**
 * Build a Mongo query matching runs whose label, target, commit or host contains the search term.
 * @param search - free-text term; blank yields an empty (match-all) query
 * @returns Mongo selector
 */
export const testRunSearchQuery = (search?: string): Mongo.Query<TestRun> => {
  search = search?.trim();
  if (!search?.length) return {};
  const regex = new RegExp(escapeRegExp(search), 'i');
  return { $or: [{ label: regex }, { targetUrl: regex }, { gitSha: regex }, { host: regex }] } as Mongo.Query<TestRun>;
};

/** What the summary needs from one run; `results` is only read on the runs the failure diff compares. */
export interface TestRunSummaryInput {
  _id: string;
  startedAt: Date;
  totals: TestRunTotals;
  passRate: number;
  durationMs?: number;
  hasPdf?: boolean;
  results?: Pick<TestRunResult, 'name' | 'status'>[];
}

export interface TestRunHeadline {
  _id: string;
  startedAt: Date;
  totals: TestRunTotals;
  passRate: number;
  durationMs?: number;
  hasPdf: boolean;
}

export interface RepeatOffender {
  name: string;
  /** Runs in the window in which this test failed. */
  failures: number;
  /** Runs in the window in which this test was executed at all. */
  runs: number;
}

export interface TestRunKindSummary {
  kind: TestRunKind;
  latest?: TestRunHeadline;
  previous?: TestRunHeadline;
  runs7d: number;
  runs30d: number;
  /** Executed (passed + failed) tests across the window's runs. */
  testsRun7d: number;
  testsRun30d: number;
  avgPassRate7d?: number;
  avgPassRate30d?: number;
  /** The 7 days before the last 7, for the week-over-week trend. */
  avgPassRatePrior7d?: number;
  /** Most recent runs, counted back from the latest, that executed tests and failed none. */
  greenStreak: number;
  /** Failing in the latest run and not in the one before. */
  newFailures: string[];
  /** Failing in both of the last two runs. */
  persistingFailures: string[];
  /** Failing in the run before and passing (or gone) in the latest. */
  resolvedFailures: string[];
  /** Tests that failed in two or more runs of the last 30 days, worst first. */
  repeatOffenders: RepeatOffender[];
}

export type TestRunsSummary = { [kind in TestRunKind]: TestRunKindSummary };

const SUMMARY_WINDOW_DAYS = 30;
const REPEAT_OFFENDER_LIMIT = 5;

const headline = (run: TestRunSummaryInput): TestRunHeadline => ({
  '_id': run._id, 'startedAt': run.startedAt, 'totals': run.totals, 'passRate': run.passRate,
  'durationMs': run.durationMs, 'hasPdf': !!run.hasPdf,
});

const average = (values: number[]): number | undefined => {
  if (!values.length) return undefined;
  return Math.round((values.reduce((sum, v) => sum + v, 0) / values.length) * 10) / 10;
};

const executed = (run: TestRunSummaryInput) => run.totals.passed + run.totals.failed;

const failedNames = (run?: TestRunSummaryInput): Set<string> => {
  const names = new Set<string>();
  for (const result of run?.results || []) if (result.status === 'failed') names.add(result.name);
  return names;
};

/**
 * The headline metrics for one kind of run. Pure, so it is testable without a database; the method hands
 * it the last 30 days of runs plus the two most recent ones, newest first or not.
 * @param kind - which kind the runs are
 * @param input - the runs, with results on at least the two most recent
 * @param now - the moment the windows are measured back from
 * @returns the summary
 */
export const summarizeTestRuns = (kind: TestRunKind, input: TestRunSummaryInput[], now = new Date()): TestRunKindSummary => {
  const runs = [...input].sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime());
  const since = (days: number) => now.getTime() - days * MILLIS_24_HOURS;
  const inWindow = (from: number, to = Number.POSITIVE_INFINITY) => runs.filter((run) => {
    const t = run.startedAt.getTime();
    return t >= from && t < to;
  });
  const last7 = inWindow(since(7));
  const prior7 = inWindow(since(14), since(7));
  const last30 = inWindow(since(SUMMARY_WINDOW_DAYS));

  let greenStreak = 0;
  for (const run of runs) {
    if (run.totals.failed > 0 || !executed(run)) break;
    greenStreak += 1;
  }

  const [latest, previous] = runs;
  const latestFailed = failedNames(latest);
  const previousFailed = failedNames(previous);
  const newFailures = [...latestFailed].filter((name) => !previousFailed.has(name));
  const persistingFailures = [...latestFailed].filter((name) => previousFailed.has(name));
  const resolvedFailures = [...previousFailed].filter((name) => !latestFailed.has(name));

  const offenders: { [name: string]: RepeatOffender } = {};
  for (const run of last30) {
    const seen = new Set<string>();
    for (const result of run.results || []) {
      if (result.status === 'skipped' || seen.has(result.name)) continue;
      seen.add(result.name);
      const offender = offenders[result.name] || (offenders[result.name] = { 'name': result.name, 'failures': 0, 'runs': 0 });
      offender.runs += 1;
      if (result.status === 'failed') offender.failures += 1;
    }
  }
  const repeatOffenders = Object.values(offenders)
    .filter((offender) => offender.failures >= 2)
    .sort((a, b) => b.failures - a.failures || a.name.localeCompare(b.name))
    .slice(0, REPEAT_OFFENDER_LIMIT);

  return {
    kind,
    'latest': latest ? headline(latest) : undefined,
    'previous': previous ? headline(previous) : undefined,
    'runs7d': last7.length,
    'runs30d': last30.length,
    'testsRun7d': last7.reduce((sum, run) => sum + executed(run), 0),
    'testsRun30d': last30.reduce((sum, run) => sum + executed(run), 0),
    'avgPassRate7d': average(last7.map((run) => run.passRate)),
    'avgPassRate30d': average(last30.map((run) => run.passRate)),
    'avgPassRatePrior7d': average(prior7.map((run) => run.passRate)),
    greenStreak,
    newFailures,
    persistingFailures,
    resolvedFailures,
    repeatOffenders,
  };
};
